import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// CERTIFICATE SERVICE — volunteer recognition certificates
// Public verify by number; volunteer "my certificates"; admin issue/list/revoke.
// ================================================================================

export type CertificateStatus = 'ACTIVE' | 'REVOKED'

/** Full record — returned to admins and to the volunteer themselves (includes email). */
export interface Certificate {
  id: number
  certificateNumber: string
  volunteerName: string
  volunteerEmail: string
  awardTitle: string
  description?: string
  programmeTag?: string
  issueDate: string
  issuedByName?: string
  status: CertificateStatus
  createdAt?: string
}

/** PII-safe public verification view — NO email field. */
export interface CertificateVerify {
  certificateNumber: string
  volunteerName: string
  awardTitle: string
  description?: string
  programmeTag?: string
  issueDate: string
  issuedByName?: string
  status: CertificateStatus
  valid: boolean
}

export interface CertificateRequest {
  volunteerName: string
  volunteerEmail: string
  awardTitle: string
  description?: string
  programmeTag?: string
  issueDate?: string
  issuedByName?: string
}

export interface CertificateStats {
  TOTAL: number
  ACTIVE: number
  REVOKED: number
}

// ---- public ----

export async function verifyCertificate(number: string): Promise<CertificateVerify> {
  const res = await api.get<ApiResponse<CertificateVerify>>(`/certificates/verify/${encodeURIComponent(number)}`)
  return res.data.data
}

// ---- volunteer (authenticated) ----

export async function myCertificates(): Promise<Certificate[]> {
  const res = await api.get<ApiResponse<Certificate[]>>(`/certificates/my`)
  return res.data.data
}

/**
 * Backfill: ensure a recognition certificate exists for every badge the signed-in
 * user has already earned (auto-awarded or leader-awarded, including badges earned
 * before certificates were auto-generated). Idempotent on the backend. Called on
 * the "My Certificates" page load so a newly-earned badge's cert appears without
 * an extra round-trip.
 */
export async function syncCertificates(): Promise<void> {
  await api.post<ApiResponse<void>>('/community/recognition/sync-certificates')
}

// ---- admin ----

export async function issueCertificate(req: CertificateRequest): Promise<Certificate> {
  const res = await api.post<ApiResponse<Certificate>>('/certificates', req)
  return res.data.data
}

export async function listCertificates(params: {
  status?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Certificate>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<Certificate>>>(`/certificates?${qs.toString()}`)
  return res.data.data
}

export async function getCertificateStats(): Promise<CertificateStats> {
  const res = await api.get<ApiResponse<CertificateStats>>(`/certificates/stats`)
  return res.data.data
}

export async function revokeCertificate(id: number): Promise<Certificate> {
  const res = await api.put<ApiResponse<Certificate>>(`/certificates/${id}/revoke`)
  return res.data.data
}

// ---- label maps ----

export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  ACTIVE: 'Active',
  REVOKED: 'Revoked',
}